import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaArrowLeft, FaPlus, FaTrash } from 'react-icons/fa';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchPayoutCards,
  setDefaultPayoutCard,
  deletePayoutCard
} from '../../store/reducers/paymentReducer';
import type { AppDispatch, RootState } from '../../store/store';
import './WithdrawalMethods.css';

const WithdrawalMethods: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const {
    payoutCards,
    isFetchingPayout,
    fetchPayoutError,
    isSettingDefaultPayout,
    isDeletingPayout
  } = useSelector((state: RootState) => state.payment);

  // Save the very first Stripe account ID once cards are fetched
  const [firstAccountId, setFirstAccountId] = useState<string>('');

  useEffect(() => {
    dispatch(fetchPayoutCards());
  }, [dispatch]);

  useEffect(() => {
    if (!isFetchingPayout && payoutCards.length > 0) {
      // Use the `account` field from the first card response
      setFirstAccountId(payoutCards[0].account);
    }
  }, [isFetchingPayout, payoutCards]);

  const handleSetDefault = () => {
    if (firstAccountId) dispatch(setDefaultPayoutCard(firstAccountId));
  };

  const handleDelete = (id: string) => {
    dispatch(deletePayoutCard(id));
  };

  return (
    <div className="wm-container">
      <div className="wm-card">
        <header className="wm-header">
          <div className="wm-header-left">
            <FaArrowLeft className="wm-back-icon" onClick={() => navigate(-1)} />
            <h2 className="wm-title">Withdrawal Methods</h2>
          </div>
          <button
            className="wm-add-card"
            onClick={() =>
              navigate('/Add-payout-card')
            }
          >
            <FaPlus className="wm-add-icon" />
            Add Card
          </button>
        </header>

        <div className="wm-body">
          {isFetchingPayout && <div className="wm-loading">Loading…</div>}
          {!isFetchingPayout && fetchPayoutError && (
            <div className="wm-error">Error: {fetchPayoutError}</div>
          )}
          {!isFetchingPayout && !fetchPayoutError && payoutCards.length === 0 && (
            <div className="wm-empty">No cards added yet</div>
          )}

          {!isFetchingPayout && !fetchPayoutError && payoutCards.length > 0 && (
            <ul className="wm-list">
              {payoutCards.map(card => (
                <li key={card.id} className="wm-card-item">
                  <div className="wm-card-details">
                    <div className="wm-card-type">
{card.brand?.toUpperCase() || 'UNKNOWN'}
                      {card.isDefault && <span className="default-badge">Default</span>}
                    </div>
                    <div className="wm-card-number">{card.last4}</div>
                    <div className="wm-card-exp">
                      Expires {String(card.expMonth).padStart(2, '0')}/{String(card.expYear).slice(-2)}
                    </div>
                  </div>
                  <button
                    className="wm-delete-btn"
                    onClick={() => handleDelete(card.id)}
                    disabled={isDeletingPayout}
                    aria-label="Delete card"
                  >
                    <FaTrash />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <footer className="wm-footer">
          <button
            className="wm-button"
            onClick={handleSetDefault}
            disabled={!firstAccountId || isSettingDefaultPayout}
          >
            {isSettingDefaultPayout ? 'Setting…' : 'Set as Default'}
          </button>
        </footer>
      </div>
    </div>
  );
};

export default WithdrawalMethods;